import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiCheckCircle, FiUpload, FiUsers, FiSearch, FiFileText, FiAward, FiArrowRight } from 'react-icons/fi';

const steps = [
  {
    id: 1,
    title: "Select Framework",
    icon: FiCheckCircle,
    desc: "Choose from pre-loaded industry standards like SOC 2, ISO 27001, or GDPR to initialize your compliance lane.",
    action: "Launch Lane"
  },
  {
    id: 2,
    title: "Assign Owners",
    icon: FiUsers,
    desc: "Distribute workload by assigning specific controls (e.g., CC1.1) to department heads or security leads.",
    action: "Delegate Control"
  },
  {
    id: 3,
    title: "Harvest Evidence",
    icon: FiUpload,
    desc: "Upload logs, policy screenshots, and reports to the Evidence Vault. Link them directly to controls.",
    action: "Add Evidence"
  },
  {
    id: 4,
    title: "Identify Gaps",
    icon: FiSearch,
    desc: "Mark controls that don't meet standards. Create gap notes and remediation timelines for internal tracking.",
    action: "Bridge Gap"
  },
  {
    id: 5,
    title: "Final Review",
    icon: FiFileText,
    desc: "The Compliance Officer reviews all linked evidence and signs off on the readiness of each control.",
    action: "Approve Control"
  },
  {
    id: 6,
    title: "Generate Report",
    icon: FiAward,
    desc: "Export a comprehensive summary of your readiness state to present to stakeholders or external auditors.",
    action: "Download Summary"
  }
];

const HowItWorks = () => {
  const [activeStep, setActiveStep] = useState(1);

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-slate-100">Try the Workflow</h1>
        <p className="text-slate-400 max-w-2xl mx-auto">
          ComplianceCloud streamlines the journey from audit anxiety to audit ready. Follow these 6 steps to master your organizational compliance.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Step Selector */}
        <div className="lg:col-span-4 space-y-2">
          {steps.map((step) => (
            <button
              key={step.id}
              onClick={() => setActiveStep(step.id)}
              className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all text-left ${
                activeStep === step.id 
                  ? 'bg-amber-500/10 border-amber-500/50 text-amber-400 shadow-[0_0_20px_rgba(245,158,11,0.1)]' 
                  : 'bg-slate-800/40 border-slate-700/50 text-slate-400 hover:bg-slate-800'
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${
                activeStep === step.id ? 'bg-amber-500 text-slate-900' : 'bg-slate-700'
              }`}>
                {step.id}
              </div>
              <span className="font-semibold">{step.title}</span>
            </button>
          ))}
        </div>

        {/* Step Detail Panel */}
        <div className="lg:col-span-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-10 min-h-[400px] flex flex-col justify-between relative overflow-hidden"
            >
              {/* Background Glow */}
              <div className="absolute -top-24 -right-24 w-64 h-64 bg-amber-500/5 blur-[80px] rounded-full"></div>
              
              <div>
                <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-500 mb-6">
                  <SafeIcon icon={steps[activeStep-1].icon} className="text-3xl" />
                </div>
                <h2 className="text-3xl font-bold text-slate-100 mb-4">{steps[activeStep-1].title}</h2>
                <p className="text-lg text-slate-400 leading-relaxed">
                  {steps[activeStep-1].desc}
                </p>
                <div className="mt-8 space-y-4">
                  <div className="flex items-center gap-3 text-sm text-slate-300 bg-slate-900/50 border border-slate-700/50 p-3 rounded-lg">
                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                    Outcome: Successful framework mapping
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-300 bg-slate-900/50 border border-slate-700/50 p-3 rounded-lg">
                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                    Tip: Use templates to save 40% setup time
                  </div>
                </div>
              </div>

              <div className="mt-10 flex justify-between items-center">
                <button className="flex items-center gap-2 text-amber-500 font-bold uppercase tracking-widest text-xs hover:gap-3 transition-all">
                  Simulate {steps[activeStep-1].action} <SafeIcon icon={FiArrowRight} />
                </button>
                <div className="flex gap-1">
                  {steps.map((_, i) => (
                    <div key={i} className={`h-1 rounded-full transition-all ${activeStep === (i+1) ? 'w-8 bg-amber-500' : 'w-2 bg-slate-700'}`}></div>
                  ))}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;